/**
 * LoadingSkeletons Component Library
 *
 * Pre-built skeleton screens for different UI patterns.
 * Uses Tailwind's animate-pulse for smooth loading animations.
 */

import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useThemeColors } from '@/styles';

/**
 * Base skeleton element with pulse animation
 */
const SkeletonBox: React.FC<{ className?: string }> = ({ className = '' }) => {
  const colors = useThemeColors();
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{
        background: `linear-gradient(90deg, ${colors.border.light} 0%, ${colors.border.default} 50%, ${colors.border.light} 100%)`,
      }}
    />
  );
};

/**
 * Card skeleton - for card-based layouts (dashboard tiles)
 */
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, index) => (
      <Card key={index} className="shadow-lg">
        <CardHeader>
          <SkeletonBox className="h-6 w-3/4 mb-2" />
          <SkeletonBox className="h-4 w-1/2" />
        </CardHeader>
        <CardContent className="space-y-3">
          <SkeletonBox className="h-4 w-full" />
          <SkeletonBox className="h-4 w-5/6" />
          <SkeletonBox className="h-4 w-4/6" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * List skeleton - for list views (items, messages)
 */
export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => {
  const colors = useThemeColors();
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="flex items-center space-x-4 p-4 rounded-lg shadow"
          style={{ backgroundColor: colors.offWhite }}
        >
          <SkeletonBox className="h-12 w-12 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBox className="h-4 w-3/4" />
            <SkeletonBox className="h-3 w-1/2" />
          </div>
          <SkeletonBox className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
};

/**
 * Table skeleton - for data tables (student lists)
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  const colors = useThemeColors();
  return (
    <div
      className="rounded-lg shadow overflow-hidden"
      style={{ backgroundColor: colors.offWhite }}
    >
      {/* Table header */}
      <div
        className="border-b p-4"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border.default,
        }}
      >
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          {Array.from({ length: columns }).map((_, index) => (
            <SkeletonBox key={index} className="h-4" />
          ))}
        </div>
      </div>

      {/* Table rows */}
      <div className="divide-y" style={{ borderColor: colors.border.default }}>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="p-4">
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
            >
              {Array.from({ length: columns }).map((_, colIndex) => (
                <SkeletonBox key={colIndex} className="h-4" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Form skeleton - for form screens (profile, settings)
 */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => (
  <Card className="shadow-lg">
    <CardHeader>
      <SkeletonBox className="h-6 w-1/3 mb-2" />
      <SkeletonBox className="h-4 w-2/3" />
    </CardHeader>
    <CardContent className="space-y-6">
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <SkeletonBox className="h-4 w-24" />
          <SkeletonBox className="h-10 w-full" />
        </div>
      ))}
      <div className="flex justify-end space-x-3 pt-4">
        <SkeletonBox className="h-10 w-24" />
        <SkeletonBox className="h-10 w-32" />
      </div>
    </CardContent>
  </Card>
);

/**
 * Dashboard skeleton - for dashboard overview
 */
export const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <SkeletonBox className="h-8 w-64" />
        <SkeletonBox className="h-4 w-48" />
      </div>
      <SkeletonBox className="h-10 w-32" />
    </div>

    {/* Stats cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <Card key={index} className="shadow">
          <CardContent className="p-6 space-y-2">
            <SkeletonBox className="h-4 w-20" />
            <SkeletonBox className="h-8 w-16" />
            <SkeletonBox className="h-3 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>

    {/* Main content area */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card className="shadow">
          <CardHeader>
            <SkeletonBox className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <SkeletonBox className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
      <div>
        <Card className="shadow">
          <CardHeader>
            <SkeletonBox className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <SkeletonBox className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <SkeletonBox className="h-3 w-full" />
                  <SkeletonBox className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

/**
 * Kanban skeleton - for kanban board
 */
export const KanbanSkeleton: React.FC = () => {
  const colors = useThemeColors();
  return (
    <div className="h-full flex flex-col px-6 py-4">
      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
        <div className="inline-flex gap-4 h-full">
          {Array.from({ length: 3 }).map((_, columnIndex) => (
            <div key={columnIndex} className="flex-shrink-0 w-80">
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: colors.border.light }}
              >
                {/* Column header */}
                <div className="flex items-center justify-between mb-4">
                  <SkeletonBox className="h-5 w-32" />
                  <SkeletonBox className="h-6 w-8 rounded-full" />
                </div>

                {/* Cards */}
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, cardIndex) => (
                    <Card key={cardIndex} className="shadow">
                      <CardContent className="p-4 space-y-3">
                        <SkeletonBox className="h-4 w-full" />
                        <SkeletonBox className="h-4 w-3/4" />
                        <div className="flex items-center justify-between pt-2">
                          <SkeletonBox className="h-6 w-16 rounded-full" />
                          <SkeletonBox className="h-6 w-6 rounded-full" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
